import { memo, useMemo } from "react";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { useEntitiesById } from "@hooks/useEntities";
import {
    EntityAvatarGroup,
    type EntityAvatarGroupEntity,
} from "@components/avatar/EntityAvatarGroup";
import { useHistoryEntityMap } from "@components/history/historyEntityMapContext";
import { HistoryRow } from "../historyViewConfig";
import styles from "./Cell.module.scss";
import { EmptyCell } from "./EmptyCell";

export const EntityCell = memo(
    ({ row }: CellContext<ViewTableFeatures, HistoryRow, unknown>) => {
        const { entityIds = [] } = row.original;
        // Workspace Activity resolves entities from the active workspace scope,
        // while organization-scoped Activity (e.g. template Activity) supplies a
        // cross-workspace map through context. Prefer the organization map when
        // present and fall back to the workspace-scoped lookup otherwise.
        const workspaceEntities = useEntitiesById();
        const organizationEntities = useHistoryEntityMap();

        const entities = useMemo(() => {
            const entitiesById = organizationEntities ?? workspaceEntities;
            return entityIds
                .map((id) => entitiesById.get(id))
                .filter(
                    (entity): entity is EntityAvatarGroupEntity =>
                        entity !== undefined,
                );
        }, [entityIds, organizationEntities, workspaceEntities]);
        if (entities.length === 0) {
            return <EmptyCell />;
        }

        return (
            <ViewTableCell className={styles.entityCell}>
                <EntityAvatarGroup entities={entities} max={3} size="xs" />
            </ViewTableCell>
        );
    },
);

EntityCell.displayName = "EntityCell";
